import { motion } from 'framer-motion'
import { Target, Heart, Users, Award, TrendingUp, BookOpen } from 'lucide-react'
import AnimatedSection from '../components/AnimatedSection'
import SectionHeading from '../components/SectionHeading'

const values = [
  {
    icon: BookOpen,
    title: 'Education First',
    description: 'We believe knowledge is the most powerful asset. Every piece of content is designed to teach, not just inform.',
  },
  {
    icon: Target,
    title: 'Discipline Over Hype',
    description: 'Trading success comes from strategy and patience, not chasing trends. We emphasize disciplined, methodical approaches.',
  },
  {
    icon: Heart,
    title: 'Community Driven',
    description: 'FiveTrader is built for the community. We learn, analyze, and grow together as a collective of passionate traders.',
  },
  {
    icon: Users,
    title: 'Accessible to All',
    description: 'From beginners taking their first steps to experienced investors refining their edge — everyone has a place here.',
  },
]

const milestones = [
  { year: '2021', title: 'Channel Launched', desc: 'FiveTrader started as a small YouTube channel sharing stock market insights.' },
  { year: '2022', title: 'Growing Community', desc: 'Reached thousands of subscribers and expanded into technical analysis deep dives.' },
  { year: '2023', title: 'Crypto Coverage', desc: 'Added cryptocurrency market education to help traders navigate the digital asset space.' },
  { year: '2024', title: 'Full Platform', desc: 'Launched the FiveTrader website to centralize all educational resources in one place.' },
]

export default function About() {
  return (
    <div className="min-h-screen pt-24 md:pt-28">
      {/* Hero */}
      <section className="relative pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-500/5 via-transparent to-brand-600/5" />
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-brand-500/10 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase bg-brand-500/10 text-brand-600 dark:text-brand-400 mb-4"
            >
              About Us
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[var(--text-primary)] mb-6 text-balance"
            >
              Empowering Traders Through <span className="gradient-text">Education</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg text-[var(--text-secondary)] leading-relaxed text-balance"
            >
              FiveTrader is an informative platform dedicated to educating investors on stock market fundamentals,
              technical analysis, and investment strategies. We cover key topics like candlestick patterns,
              market trends, and trading techniques, empowering both beginners and experienced investors.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-[var(--bg-secondary)] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="glass rounded-3xl p-8 md:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-brand-500/10 flex items-center justify-center">
                    <TrendingUp size={20} className="text-brand-600 dark:text-brand-400" />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--text-primary)]">Our Mission</h3>
                </div>
                <p className="text-[var(--text-secondary)] leading-relaxed mb-4">
                  Learning about the stock market with discipline helps build long-term growth by fostering
                  a strategic mindset, reducing impulsive decisions, and improving risk management.
                </p>
                <p className="text-[var(--text-secondary)] leading-relaxed">
                  Mastery of these skills allows investors to navigate market volatility with confidence
                  and make well-informed decisions for sustained financial success. Let us learn and grow together.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="glass rounded-3xl p-8 md:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-brand-500/10 flex items-center justify-center">
                    <Award size={20} className="text-brand-600 dark:text-brand-400" />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--text-primary)]">Our Vision</h3>
                </div>
                <p className="text-[var(--text-secondary)] leading-relaxed mb-4">
                  We envision a world where every individual has access to quality financial education,
                  enabling them to take control of their financial future through informed investing.
                </p>
                <p className="text-[var(--text-secondary)] leading-relaxed">
                  By democratizing trading knowledge, we aim to bridge the gap between Wall Street
                  complexity and Main Street accessibility.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            subtitle="Our Values"
            title="What Drives FiveTrader"
            description="Our core principles shape every video, analysis, and strategy we share with our community."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((val, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="glass rounded-3xl p-8 flex items-start gap-5 card-hover glow-border">
                  <div className="w-12 h-12 rounded-2xl bg-brand-500/10 flex items-center justify-center shrink-0">
                    <val.icon size={22} className="text-brand-600 dark:text-brand-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[var(--text-primary)] mb-2">{val.title}</h4>
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{val.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 md:py-32 bg-[var(--bg-secondary)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            subtitle="Our Journey"
            title="The FiveTrader Story"
            description="From a single YouTube video to a thriving educational platform — here is how we grew."
          />
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-brand-500/50 via-brand-500/20 to-transparent" />
            {milestones.map((m, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <div className={`relative flex items-start gap-6 md:gap-12 mb-12 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className="hidden md:block w-1/2 text-right">
                    {i % 2 === 0 && (
                      <div className="glass rounded-2xl p-6 inline-block text-left">
                        <span className="text-brand-600 dark:text-brand-400 font-bold text-sm">{m.year}</span>
                        <h4 className="text-lg font-semibold text-[var(--text-primary)] mt-1">{m.title}</h4>
                        <p className="text-sm text-[var(--text-secondary)] mt-2">{m.desc}</p>
                      </div>
                    )}
                  </div>
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-brand-500 ring-4 ring-brand-500/20 mt-2" />
                  <div className="ml-10 md:ml-0 md:w-1/2">
                    <div className="glass rounded-2xl p-6 md:hidden">
                      <span className="text-brand-600 dark:text-brand-400 font-bold text-sm">{m.year}</span>
                      <h4 className="text-lg font-semibold text-[var(--text-primary)] mt-1">{m.title}</h4>
                      <p className="text-sm text-[var(--text-secondary)] mt-2">{m.desc}</p>
                    </div>
                    {i % 2 !== 0 && (
                      <div className="glass rounded-2xl p-6 hidden md:block">
                        <span className="text-brand-600 dark:text-brand-400 font-bold text-sm">{m.year}</span>
                        <h4 className="text-lg font-semibold text-[var(--text-primary)] mt-1">{m.title}</h4>
                        <p className="text-sm text-[var(--text-secondary)] mt-2">{m.desc}</p>
                      </div>
                    )}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
